<template>
  <span>
    <AppSelectMultipleDatesCheckbox
      v-if="showCheckboxesView()"
      :date-selections="dateSelections"
      :label="
        $t('calendar:filters.time.label', 'Time', 'Label of the time filter')
      "
      :show-date="showDate"
      :is-custom="
        Boolean(
          (dateFrom && !dateFrom.includes('01-01')) ||
            (dateUntil && !dateUntil.includes('12-31'))
        )
      "
      @setTime="$event => setTime($event.dateFrom, $event.dateUntil)"
      @advancedState="() => (advancedState = true)"
    />

    <v-menu
      v-else
      v-model="showMenu"
      :close-on-content-click="false"
      :close-on-click="!advancedState"
      disable-keys
      allow-overflow
      eager
      :nudge-bottom="nudgeBottom"
      max-height="520px"
    >
      <template #activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on">
          <v-text-field
            v-show-fade="{ isFilter: isFilter }"
            :value="showDate()"
            v-bind="[$attrs]"
            :class="[selectMixinClass, 'multiple-date']"
            :clearable="clearable"
            append-icon="$iconCalendar"
            hide-details
            outlined
            readonly
            input-type="dates"
            style="cursor: pointer"
            @change="inputVisibility($event, 'change')"
            @focus="focused"
            @blur="inputVisibility"
            @click:clear="clear"
          />
        </div>
      </template>
      <v-card color="white">
        <v-list v-show="!advancedState" dense subheader>
          <v-list-item flat @click.prevent="advancedState = true">
            <v-list-item-content>
              <v-btn text color="accent" block>
                <v-icon left>$iconCalendar</v-icon>
                {{ $t("common:DatePicker.CustomTime.label") }}
              </v-btn>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
          <v-list-item
            v-for="date in dateSelections"
            :key="date.number"
            @click.prevent="setTime(date.dateFrom, date.dateUntil)"
          >
            <v-list-item-content>{{ date.text }}</v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
    <component
      :is="$vuetify.breakpoint.mdAndUp ? 'div' : 'v-dialog'"
      v-show="advancedState"
      :value="advancedState"
      :class="{ 'absolute-date-position': $vuetify.breakpoint.mdAndUp }"
      style="z-index: 200"
    >
      <v-card
        :tile="$vuetify.breakpoint.smAndDown"
        :class="{
          'pa-5': $vuetify.breakpoint.smAndDown
        }"
      >
        <div class="date-selection-container">
          <v-row no-gutters class="mb-2">
            <v-col>
              <v-btn
                depressed
                block
                class="px-1"
                :x-large="$vuetify.breakpoint.smAndDown"
                @click.native="defaultDate()"
              >
                <v-icon color="secondary" small class="mr-3"
                  >$iconRefresh</v-icon
                >
                {{ $t("common:DatePicker.Reset.button") }}
              </v-btn>
            </v-col>
            <v-col class="text-right">
              <v-btn
                icon
                :aria-label="$t('common:closeButton.alt', 'Close')"
                :x-large="$vuetify.breakpoint.smAndDown"
                @click.native="[(advancedState = false), (showMenu = true)]"
              >
                <v-icon>$iconTimesCircle</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <div class="px-2 pt-2">
            <AppDatePicker
              v-model="dateStart"
              :label="$t('common:DatePicker.StartDate.label')"
              show-current
              hide-details
              autofocus
            />
            <br />
            <AppDatePicker
              ref="endDate"
              v-model="dateEnd"
              :min="dateStart"
              :max="dateMax"
              :label="$t('common:DatePicker.EndDate.label')"
              :disabled="!dateStart"
              hide-details
            />
            <br />
            <v-row no-gutters>
              <v-col class="text-center">
                <v-btn
                  large
                  block
                  outlined
                  :x-large="$vuetify.breakpoint.smAndDown"
                  @click="setTime(dateStart, dateEnd)"
                >
                  {{ $t("common:DatePicker.Apply.button") }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card>
    </component>
  </span>
</template>
<script>
import { defineComponent } from "vue";
import selectsMixin from "./selects_mixin";
import i18next from "i18next";
import AppDatePicker from "@elements/AppDatePicker.vue";
import AppSelectMultipleDatesCheckbox from "./AppSelectMultipleDatesCheckbox.vue";

export default defineComponent({
  name: "AppSelectMultipleDates",
  components: { AppDatePicker, AppSelectMultipleDatesCheckbox },
  mixins: [selectsMixin],
  props: {
    dateFrom: {
      type: String,
      default: null
    },
    dateUntil: {
      type: String,
      default: null
    },
    ignoreFutureYear: {
      type: Boolean,
      default: false
    },
    defaultDateFrom: {
      type: Array,
      default: () => []
    },
    defaultUntilToday: {
      type: Boolean,
      default: false
    },
    dateMin: {
      type: String,
      default: null
    },
    dateMax: {
      type: String,
      default: null
    },
    defaultDateUntil: {
      type: Array,
      default: () => []
    },
    customDefaultDate: {
      type: Boolean,
      default: false
    },
    dateOptions: {
      type: Array,
      default: () => []
    },
    clearable: {
      type: Boolean,
      default: true
    },
    ignoreDefaultDate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showMenu: false,
      dateStart: null,
      dateEnd: null,
      advancedState: false,
      // Don't show All at all in selects_mixin
      disableAll: true
    };
  },
  computed: {
    dateSelections() {
      let currentYear = this.$dayjs().format("YYYY");

      if (this.dateOptions.length > 0) {
        return this.dateOptions.map(date => {
          switch (date.type) {
            case "wholeYear":
              return {
                dateFrom: currentYear + "-01-01",
                dateUntil: currentYear + "-12-31",
                text: currentYear
              };
            case "year":
              return {
                dateFrom: date.year + "-01-01",
                dateUntil: date.year + "-12-31",
                text: date.year
              };
            case "custom":
              return {
                dateFrom: date.dateFrom,
                dateUntil: date.dateUntil,
                text: date.text
              };

            default:
              return {
                dateFrom: this.$dayjs()
                  .subtract(date.number, date.type)
                  .format("YYYY-MM-DD"),
                dateUntil: this.$dayjs().format("YYYY-MM-DD"),
                text: date.text
              };
          }
        });
      }

      currentYear = Number(currentYear);
      let gap = 1;
      if (this.ignoreFutureYear) gap = 0;
      let years = [currentYear + gap, currentYear, currentYear - 1];

      return years.map(year => {
        return {
          dateFrom: year + "-01-01",
          dateUntil: year + "-12-31",
          text: year
        };
      });
    }
  },
  watch: {
    showMenu(bool) {
      if (bool === false) {
        this.advanced = false;
      }
    },
    dateStart(newDateValue) {
      if (!newDateValue) return;
      if (!this.dateEnd || this.$dayjs(newDateValue).isAfter(this.dateEnd)) {
        var today = this.$dayjs();
        // If dateStart is behind today's date, then give dateEnd today's date
        if (today.isAfter(newDateValue)) {
          this.dateEnd = this.$dayjs(newDateValue)
            .endOf("year")
            .format("YYYY-MM-DD");
        } else {
          this.dateEnd = newDateValue;
        }
      }
    }
  },
  created() {
    i18next.on("languageChanged", this.refreshLocaleDate);
    if (!this.dateFrom && !this.dateUntil && !this.ignoreDefaultDate) {
      this.defaultDate();
    }
  },
  destroyed() {
    i18next.off("languageChanged", this.refreshLocaleDate);
  },
  methods: {
    refreshLocaleDate() {
      this.$forceUpdate();
    },
    showSelectedDate() {
      const valueText = date => {
        if (this.$dayjs(date).isSame(this.$dayjs(), "day")) {
          return this.$t("common:DatePicker.Today.label", "Today");
        } else if (date) {
          return this.$dayjs.date(date);
        }
        return "...";
      };

      const translatedToday = this.$t("common:DatePicker.Today.label", "Today");
      const start = valueText(this.dateFrom);
      const end = valueText(this.dateUntil);

      if (start == translatedToday && end == translatedToday) {
        return translatedToday;
      } else {
        return start + " - " + end;
      }
    },
    showDate() {
      if (!this.dateFrom && !this.dateUntil) return "";
      else return this.showSelectedDate();
    },
    defaultDate() {
      if (this.ignoreDefaultDate) return;
      if (this.customDefaultDate) {
        this.$emit("defaultDate");
        return;
      }
      this.dateStart = null;
      this.dateEnd = null;

      if (this.defaultDateFrom.length >= 2) {
        const [number, type] = this.defaultDateFrom;
        this.$emit(
          "update:dateFrom",
          this.$dayjs().subtract(number, type).format("YYYY-MM-DD")
        );
      } else if (this.defaultDateFrom.length == 1) {
        this.$emit("update:dateFrom", this.defaultDateFrom[0]);
      } else {
        this.$emit("update:dateFrom", this.$dayjs().format("YYYY-MM-DD"));
      }

      if (this.defaultDateUntil.length >= 2) {
        const [number, type] = this.defaultDateUntil;
        this.$emit(
          "update:dateUntil",
          this.$dayjs().add(number, type).format("YYYY-MM-DD")
        );
      } else if (this.defaultDateUntil.length == 1) {
        this.$emit("update:dateUntil", this.defaultDateUntil[0]);
      }

      if (this.defaultUntilToday) {
        this.$emit("update:dateUntil", this.$dayjs().format("YYYY-MM-DD"));
      } else if (!this.defaultDateUntil) {
        this.$emit("update:dateUntil", this.$dayjs().format("YYYY") + "-12-31");
      }
    },
    setTime(from, until) {
      if (from && until) {
        this.$emit("update:dateFrom", from);
        this.$emit("update:dateUntil", until);
      } else {
        this.$emit("update:dateFrom", this.dateStart);
        this.$emit("update:dateUntil", this.dateEnd);
      }
      this.advancedState = false;
    },
    clear() {
      this.$emit("update:dateFrom", null);
      this.$emit("update:dateUntil", null);
    }
  }
});
</script>
<style lang="scss">
@media (min-width: 992px) {
  .absolute-date-position {
    position: absolute;

    & > div {
      min-width: 285px;
    }
  }
}

.date-selection-container {
  padding: 16px;

  @media (max-width: 992px) {
    margin: auto;
    max-width: 480px;
  }
}

.multiple-date {
  .v-input__slot input {
    padding-bottom: 0 !important;
  }
}
</style>
